import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Truck, ShieldCheck, Star, Sparkles, ArrowRight, ArrowDown, Quote,
    IndianRupee, HeartHandshake, BadgeCheck, Droplets, Check, X, Minus,
} from 'lucide-react';
import {
    motion, useReducedMotion, useMotionValue, useSpring, useScroll,
    useTransform, useMotionValueEvent, AnimatePresence,
} from 'framer-motion';
import api from '@/lib/api';
import { resolveImage, formatINR } from '@/lib/format';
import ProductCard from '@/components/ProductCard';
import { Reveal, Magnetic, Parallax, KineticText, CountUp, Logo, Tilt } from '@/components/motion/Primitives';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';

const EASE = [0.16, 1, 0.3, 1];

/* ------------------------------------------------------------------ data */
const CATEGORIES = [
    { name: 'Laundry Care', desc: 'Detergents, powders & softeners for brighter, softer clothes.', image: '/products/detergent-liquid-bottle.jpg', to: '/shop?category=Laundry%20Care', span: 'lg:col-span-5 lg:row-span-2', big: true },
    { name: 'Surface & Glass', desc: 'Streak-free, crystal-clear shine.', image: '/products/glass-cleaner-real.jpg', to: '/shop?category=Surface%20%26%20Glass', span: 'lg:col-span-4' },
    { name: 'Dishwashing', desc: 'Cuts grease fast.', image: '/products/dishwash-gel-real.jpg', to: '/shop?category=Dishwashing', span: 'lg:col-span-3' },
    { name: 'Bathroom & Utility', desc: 'Deep clean & descale.', image: '/products/bathroom-gel.jpg', to: '/shop?category=Bathroom%20%26%20Utility', span: 'lg:col-span-3' },
    { name: 'Hand Wash', desc: 'Soft, germ-free hands.', image: '/products/handwash-luxe.jpg', to: '/shop?category=Hand%20Wash', span: 'lg:col-span-4' },
];

const PILLARS = [
    { icon: Sparkles, title: 'Xtra Clean', tag: 'Performance', desc: 'Demo-proven cleaning power that beats India\u2019s toughest dirt, grease and stains in a single wash.', img: '/products/detergent-powder.jpg' },
    { icon: IndianRupee, title: 'Xtra Value', tag: 'Honest pricing', desc: 'Honest weight, honest price and better per-wash math \u2014 the smartest buy on the shelf, not the cheapest.', img: '/products/detergent-liquid-bottle.jpg' },
    { icon: HeartHandshake, title: 'Xtra Care', tag: 'Gentle formulas', desc: 'Kind to your fabrics, gentle on your hands and safe for your machine and your family.', img: '/products/handwash-moisture.jpg' },
    { icon: BadgeCheck, title: 'Xtra Bharosa', tag: 'Since 1987', desc: 'Made in ISO-certified plants and trusted by millions of Indian families for over three decades.', img: '/products/fabcon-intense-luxe.jpg' },
];

const STORY = [
    { kicker: 'Kitchen', head: 'Grease? Gone.', copy: 'Cuts through ghee, masala and stubborn oil in one wash \u2014 no soaking, no scrubbing.', img: '/products/dishwash-gel-real.jpg', product: 'Dish Wash Gel' },
    { kicker: 'Laundry', head: 'Stains? Xtra Saaf.', copy: 'Deep-clean power lifts sweat, mud and food stains while keeping whites bright.', img: '/products/detergent-liquid-bottle.jpg', product: 'Liquid Detergent' },
    { kicker: 'Glass', head: 'Streaks? Never.', copy: 'A crystal-clear, streak-free shine on mirrors, windows and glass tops.', img: '/products/glass-cleaner-real.jpg', product: 'Glass Cleaner' },
    { kicker: 'Hands', head: 'Soft on skin.', copy: 'Kills 99.9% of germs while moisturising \u2014 gentle enough for the whole family.', img: '/products/handwash-luxe.jpg', product: 'Hand Wash' },
];

const TESTIMONIALS = [
    { name: 'Ananya R.', city: 'Bengaluru', text: 'The dishwash gel cuts through ghee and masala instantly. My utensils have never been this squeaky clean.' },
    { name: 'Rahul M.', city: 'Pune', text: 'Switched my whole house to XTRA. The liquid detergent smells amazing and clothes feel genuinely softer.' },
    { name: 'Fatima S.', city: 'Hyderabad', text: 'The bathroom gel is a game changer \u2014 thick, reaches under the rim and leaves the bathroom truly fresh.' },
    { name: 'Vikram P.', city: 'Delhi', text: 'Premium packaging, honest pricing and it actually works. The glass cleaner leaves zero streaks.' },
    { name: 'Meera K.', city: 'Chennai', text: 'Value for money is unreal. One bottle lasts so long and the fragrance stays on clothes for days.' },
];

const MARQUEE = ['Daam Wahi. Dum Xtra.', 'India Ka Xtra.', 'Sirf Saaf Nahi \u2014 Xtra Saaf.', 'Har Din, Thoda Xtra.', 'Trusted Since 1987.'];

const RIVALS = ['Tide', 'Surf Excel', 'Rin'];
// per-rival mark: 'no' (cross) | 'part' (limited) | 'yes' (check)
const COMPARE_ROWS = [
    { f: 'Complete home-care range', sub: 'laundry, dishes, glass, bathroom & hands', rivals: ['no', 'no', 'no'] },
    { f: 'Kills 99.9% germs across the home', rivals: ['no', 'part', 'no'] },
    { f: 'Gentle on hands, skin & fabrics', rivals: ['part', 'part', 'part'] },
    { f: 'Long-lasting premium fragrance', rivals: ['part', 'part', 'part'] },
    { f: 'Honest per-wash value pricing', rivals: ['part', 'part', 'no'] },
    { f: 'Made in India \u00b7 trusted since 1987', rivals: ['no', 'part', 'part'] },
];

const XTRA_TINT = { backgroundColor: 'color-mix(in srgb, var(--xtra-blue) 6%, #fff)' };

const Mark = ({ state }) => {
    if (state === 'yes') return (
        <span className="h-8 w-8 rounded-full bg-[var(--xtra-yellow)] grid place-items-center shadow-[var(--shadow-tight)]" aria-label="Yes">
            <Check className="h-[18px] w-[18px] text-black" strokeWidth={3} />
        </span>
    );
    if (state === 'part') return (
        <span className="h-8 w-8 rounded-full bg-[var(--bg-soft)] border border-[var(--border)] grid place-items-center" aria-label="Limited">
            <Minus className="h-4 w-4 text-[var(--text-muted)]" strokeWidth={2.5} />
        </span>
    );
    return (
        <span className="h-8 w-8 rounded-full bg-white border border-[var(--border)] grid place-items-center" aria-label="No">
            <X className="h-4 w-4 text-[var(--text-muted)]/70" strokeWidth={2.5} />
        </span>
    );
};

const GRID = 'grid grid-cols-[minmax(148px,1.4fr)_repeat(4,minmax(56px,1fr))]';

/* ------------------------------------------------- head-to-head comparison */
const Comparison = () => (
    <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
        <Reveal>
            <div className="max-w-2xl mx-auto text-center">
                <p className="kicker text-[var(--xtra-blue)]">How Xtra compares</p>
                <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.03] text-3xl sm:text-4xl lg:text-5xl">
                    More than a detergent
                </h2>
                <p className="mt-4 text-[var(--text-muted)] text-base sm:text-lg leading-relaxed">
                    Tide, Surf Excel and Rin clean your laundry. Xtra brings that same trusted power to every corner of your home — for less per use.
                </p>
            </div>
        </Reveal>

        <Reveal delay={0.1}>
            <div className="mt-10 sm:mt-12 max-w-3xl mx-auto overflow-x-auto no-scrollbar">
                <div
                    data-testid="comparison-table"
                    className="min-w-[600px] sm:min-w-0 rounded-[var(--radius-lg)] border border-[var(--border)] bg-white shadow-[var(--shadow-soft)] overflow-hidden"
                >
                    {/* header */}
                    <div className={`${GRID} items-stretch`}>
                        <div className="p-4 sm:p-5 flex items-end">
                            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">The comparison</span>
                        </div>
                        <div className="bg-[var(--xtra-blue)] grid place-items-center p-3 sm:p-4">
                            <Logo variant="dark" className="h-4 sm:h-5" />
                        </div>
                        {RIVALS.map((r) => (
                            <div key={r} className="bg-[var(--bg-soft)] grid place-items-center p-3 sm:p-4 text-center border-l border-[var(--border)]">
                                <span className="text-[11px] sm:text-[13px] font-bold leading-tight text-[var(--text-muted)]">{r}</span>
                            </div>
                        ))}
                    </div>

                    {/* rows */}
                    {COMPARE_ROWS.map((row, i) => (
                        <div key={row.f} className={`${GRID} items-center border-t border-[var(--border)] ${i % 2 === 1 ? 'bg-[var(--bg-soft)]/40' : ''}`}>
                            <div className="p-4 sm:p-5">
                                <p className="text-sm sm:text-[15px] font-semibold text-black leading-snug">{row.f}</p>
                                {row.sub && <p className="text-xs text-[var(--text-muted)] mt-0.5 leading-snug">{row.sub}</p>}
                            </div>
                            <div className="grid place-items-center p-3.5 sm:p-5 self-stretch" style={XTRA_TINT}><Mark state="yes" /></div>
                            {row.rivals.map((s, j) => (
                                <div key={j} className="grid place-items-center p-3.5 sm:p-5 border-l border-[var(--border)]"><Mark state={s} /></div>
                            ))}
                        </div>
                    ))}

                    {/* footer CTA */}
                    <div className="border-t border-[var(--border)] p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4 bg-[var(--xtra-blue)]">
                        <p className="font-display font-bold text-white text-lg leading-tight">One brand for the whole home — that's the Xtra difference.</p>
                        <Link to="/shop" data-testid="comparison-cta" className="btn-accent h-11 px-6 text-sm shrink-0">
                            Shop the range <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </div>
        </Reveal>

        <p className="mt-4 max-w-3xl mx-auto text-center text-[11px] text-[var(--text-muted)] leading-relaxed px-2">
            *Reflects Xtra's promises across its full home-care range. Tide, Surf Excel and Rin are trademarks of their respective owners, shown for identification only; individual products and variants may differ.
        </p>
    </section>
);

/* ------------------------------------------------- per-wash savings calc */
const XTRA_PER_WASH = 7;       // ₹ per wash with Xtra (illustrative)
const ORDINARY_PER_WASH = 12;  // ₹ per wash, typical premium brand (illustrative)

const SavingsCalculator = () => {
    const reduce = useReducedMotion();
    const [loads, setLoads] = useState(7); // washes per week
    const monthlyFactor = 4.33;
    const monthlySave = Math.round((ORDINARY_PER_WASH - XTRA_PER_WASH) * loads * monthlyFactor);
    const yearlySave = monthlySave * 12;
    const xtraSpend = Math.round(XTRA_PER_WASH * loads * monthlyFactor);
    const ordSpend = Math.round(ORDINARY_PER_WASH * loads * monthlyFactor);
    const xtraPct = Math.max(8, Math.round((xtraSpend / ordSpend) * 100));

    return (
        <section id="savings" className="relative overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                    {/* Left: copy + control */}
                    <div>
                        <Reveal>
                            <p className="kicker text-[var(--xtra-blue)]">Per-wash savings</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.03] text-3xl sm:text-4xl lg:text-5xl">
                                See what your home saves
                            </h2>
                            <p className="mt-4 text-[var(--text-muted)] text-base sm:text-lg leading-relaxed max-w-lg">
                                Slide to match your weekly washes. Premium results shouldn't cost a premium — here's the difference Xtra makes.
                            </p>
                        </Reveal>

                        <Reveal delay={0.08}>
                            <div className="mt-8 rounded-[var(--radius-lg)] border border-[var(--border)] bg-white shadow-[var(--shadow-soft)] p-6 sm:p-7">
                                <div className="flex items-end justify-between">
                                    <label htmlFor="wash-slider" className="text-sm font-semibold text-black">Washes per week</label>
                                    <span data-testid="savings-loads-value" className="font-display font-extrabold text-2xl text-[var(--xtra-blue)] tabular-nums">{loads}</span>
                                </div>
                                <Slider
                                    id="wash-slider"
                                    data-testid="savings-slider"
                                    value={[loads]}
                                    min={1}
                                    max={21}
                                    step={1}
                                    onValueChange={(v) => setLoads(v[0])}
                                    className="mt-5 [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 [&_[role=slider]]:border-2 [&_[role=slider]]:border-[var(--xtra-blue)] [&_[role=slider]]:bg-white [&_[role=slider]]:shadow-[var(--shadow-tight)] [&>span:first-child]:h-2.5 [&>span:first-child]:bg-[var(--bg-soft)]"
                                />
                                <div className="mt-2 flex justify-between text-xs text-[var(--text-muted)]"><span>1</span><span>Every day</span><span>21</span></div>

                                {/* mini spend bars */}
                                <div className="mt-7 space-y-4">
                                    <div>
                                        <div className="flex items-center justify-between text-sm mb-1.5">
                                            <span className="font-semibold text-black">With Xtra</span>
                                            <span className="tabular-nums font-semibold text-black">{formatINR(xtraSpend)}/mo</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-[var(--bg-soft)] overflow-hidden">
                                            <motion.div className="h-full rounded-full bg-[var(--xtra-blue)]" animate={{ width: `${xtraPct}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between text-sm mb-1.5">
                                            <span className="font-semibold text-[var(--text-muted)]">Ordinary cleaners</span>
                                            <span className="tabular-nums font-semibold text-[var(--text-muted)]">{formatINR(ordSpend)}/mo</span>
                                        </div>
                                        <div className="h-3 rounded-full bg-[var(--bg-soft)] overflow-hidden">
                                            <div className="h-full w-full rounded-full bg-[var(--text-muted)]/35" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Right: animated savings card */}
                    <Reveal delay={0.12}>
                        <Tilt max={7} className="relative">
                            <div className="relative overflow-hidden rounded-[var(--radius-lg)] royal-panel p-8 sm:p-10 shadow-[var(--shadow-blue)]">
                                <div className="noise-overlay" />
                                <div className="relative" style={{ transform: 'translateZ(40px)' }}>
                                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold text-white">
                                        <Sparkles className="h-3.5 w-3.5 text-[var(--xtra-yellow)]" /> Your estimated saving
                                    </div>
                                    <div className="mt-5 flex items-end gap-2">
                                        <motion.span
                                            key={monthlySave}
                                            initial={reduce ? false : { scale: 0.9 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                                            data-testid="savings-monthly"
                                            className="font-display font-extrabold tracking-tight text-[var(--xtra-yellow)] text-6xl sm:text-7xl leading-none tabular-nums"
                                        >
                                            {formatINR(monthlySave)}
                                        </motion.span>
                                        <span className="text-white/80 font-semibold pb-2">/ month</span>
                                    </div>
                                    <p className="mt-4 text-white/85">
                                        That's about{' '}
                                        <span data-testid="savings-yearly" className="font-bold text-white tabular-nums">{formatINR(yearlySave)}</span>{' '}
                                        back in your pocket every year.
                                    </p>
                                    <Link to="/shop" data-testid="savings-cta" className="btn-accent h-12 px-7 mt-7 inline-flex">
                                        Start saving with Xtra <ArrowRight className="h-4 w-4" />
                                    </Link>
                                    <p className="mt-5 text-[11px] text-white/50 leading-relaxed">
                                        *Illustrative estimate based on average per-wash cost (Xtra ₹{XTRA_PER_WASH} vs typical premium ₹{ORDINARY_PER_WASH}). Actual savings vary by product & usage.
                                    </p>
                                </div>
                            </div>
                        </Tilt>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------- hero montage */
const HeroMontage = () => {
    const reduce = useReducedMotion();
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 120, damping: 18, mass: 0.3 });
    const sry = useSpring(ry, { stiffness: 120, damping: 18, mass: 0.3 });

    const onMove = (e) => {
        if (reduce) return;
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 12);
        rx.set(-py * 10);
    };
    const onLeave = () => { rx.set(0); ry.set(0); };

    return (
        <div className="relative" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={onLeave}>
            <motion.div
                style={{ rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d' }}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: EASE }}
                className="relative"
            >
                <div className={reduce ? '' : 'floaty'}>
                    <img
                        src="/hero-real.webp"
                        alt="The XTRA home-care range — detergent, dishwash, fabric conditioner and more"
                        className="w-full h-auto object-contain drop-shadow-[0_40px_60px_rgba(13,2,120,0.28)]"
                        fetchpriority="high"
                    />
                </div>

                {/* Floating chips */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6, ease: EASE }}
                    className="absolute -left-2 sm:left-2 top-6 glass rounded-2xl px-3.5 py-2.5 shadow-[var(--shadow-soft)] flex items-center gap-2.5"
                    style={{ transform: 'translateZ(40px)' }}
                >
                    <span className="h-9 w-9 rounded-full bg-[var(--xtra-yellow)] grid place-items-center"><Star className="h-5 w-5 text-black fill-black" /></span>
                    <span><span className="block text-sm font-extrabold leading-none tabular-nums">4.9★</span><span className="block text-[11px] text-[var(--text-muted)] mt-0.5">12,000+ reviews</span></span>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.6, ease: EASE }}
                    className="absolute -right-1 sm:right-3 bottom-8 glass rounded-2xl px-3.5 py-2.5 shadow-[var(--shadow-soft)] flex items-center gap-2.5"
                    style={{ transform: 'translateZ(40px)' }}
                >
                    <span className="h-9 w-9 rounded-full bg-[var(--xtra-blue)] grid place-items-center"><Droplets className="h-5 w-5 text-white" /></span>
                    <span><span className="block text-sm font-extrabold leading-none">Kills 99.9%</span><span className="block text-[11px] text-[var(--text-muted)] mt-0.5">germs & bacteria</span></span>
                </motion.div>
            </motion.div>
        </div>
    );
};

/* --------------------------------------------------------------- pillars UI */
const Pillars = () => {
    const [active, setActive] = useState(0);
    const p = PILLARS[active];
    return (
        <section id="why" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28">
            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
                <div>
                    <Reveal>
                        <p className="kicker text-[var(--xtra-blue)]">Why XTRA</p>
                        <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl">
                            Four reasons families<br className="hidden sm:block" /> never switch back.
                        </h2>
                    </Reveal>
                    <div className="mt-8 flex flex-col gap-2">
                        {PILLARS.map((item, i) => {
                            const on = i === active;
                            return (
                                <button
                                    key={item.title}
                                    data-testid={`pillar-tab-${i}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    className={`group text-left rounded-2xl border p-4 sm:p-5 transition-colors ${on ? 'border-[var(--xtra-blue)] bg-[var(--bg-soft)]' : 'border-[var(--border)] bg-white hover:bg-[var(--bg-soft)]'}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <span className={`h-11 w-11 rounded-xl grid place-items-center shrink-0 transition-colors ${on ? 'bg-[var(--xtra-blue)] text-white' : 'bg-[var(--bg-soft)] text-[var(--xtra-blue)]'}`}>
                                            <item.icon className="h-5 w-5" />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <h3 className="font-display font-bold text-lg">{item.title}</h3>
                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{item.tag}</span>
                                            </div>
                                            <AnimatePresence initial={false}>
                                                {on && (
                                                    <motion.p
                                                        initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3, ease: EASE }}
                                                        className="text-sm text-[var(--text-muted)] overflow-hidden"
                                                    >
                                                        <span className="block pt-2">{item.desc}</span>
                                                    </motion.p>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Reveal delay={0.1}>
                    <div className="relative rounded-[var(--radius-lg)] overflow-hidden product-stage aspect-[4/5] sm:aspect-square">
                        <div className="absolute inset-0 hero-wash opacity-40" />
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={p.img}
                                src={resolveImage(p.img)}
                                alt={p.title}
                                loading="lazy"
                                decoding="async"
                                initial={{ opacity: 0, scale: 0.92, y: 16 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: -16 }}
                                transition={{ duration: 0.4, ease: EASE }}
                                className="absolute inset-0 h-full w-full object-contain p-10 sm:p-14 drop-shadow-[0_30px_50px_rgba(13,2,120,0.22)]"
                            />
                        </AnimatePresence>
                        <div className="absolute left-6 bottom-6 z-10 glass rounded-2xl px-4 py-3">
                            <p className="font-display font-extrabold text-lg leading-none">{p.title}</p>
                            <p className="text-xs text-[var(--text-muted)] mt-1">{p.tag}</p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------- sticky story */
const StickyStory = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const [active, setActive] = useState(0);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        const idx = Math.min(STORY.length - 1, Math.max(0, Math.floor(v * STORY.length)));
        setActive(idx);
    });

    if (reduce) {
        return (
            <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
                <h2 className="font-display font-extrabold text-3xl lg:text-4xl">One brand. Every corner of your home.</h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                    {STORY.map((s) => (
                        <div key={s.head} className="rounded-2xl border border-[var(--border)] p-6 flex gap-4 items-center">
                            <img src={resolveImage(s.img)} alt={s.product} loading="lazy" decoding="async" className="h-24 w-auto object-contain" />
                            <div><p className="kicker text-[var(--xtra-blue)]">{s.kicker}</p><h3 className="font-display font-bold text-xl mt-1">{s.head}</h3><p className="text-sm text-[var(--text-muted)] mt-1">{s.copy}</p></div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section ref={ref} className="relative bg-[var(--bg-soft)]" style={{ height: `${STORY.length * 100}vh` }}>
            <div className="sticky top-0 h-screen overflow-hidden flex items-center">
                <div className="absolute inset-0 section-wash" />
                <div className="relative max-w-[1280px] mx-auto w-full px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-8 items-center">
                    {/* left: steps */}
                    <div>
                        <p className="kicker text-[var(--xtra-blue)]">The XTRA range</p>
                        <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl">
                            One brand.<br />Every corner of<br />your home.
                        </h2>
                        <div className="mt-8 space-y-1">
                            {STORY.map((s, i) => {
                                const on = i === active;
                                return (
                                    <div key={s.head} className={`flex items-start gap-4 rounded-2xl px-4 py-3 transition-colors ${on ? 'bg-white shadow-[var(--shadow-tight)]' : ''}`}>
                                        <span className={`mt-1 h-7 w-7 rounded-full grid place-items-center text-xs font-extrabold shrink-0 transition-colors ${on ? 'bg-[var(--xtra-blue)] text-white' : 'bg-white border border-[var(--border)] text-[var(--text-muted)]'}`}>{i + 1}</span>
                                        <div>
                                            <h3 className={`font-display font-bold text-lg transition-colors ${on ? 'text-black' : 'text-[var(--text-muted)]'}`}>{s.head}</h3>
                                            <AnimatePresence initial={false}>
                                                {on && (
                                                    <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden text-sm text-[var(--text-muted)]">
                                                        <span className="block pt-1">{s.copy}</span>
                                                    </motion.p>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* right: product stage */}
                    <div className="relative h-[46vh] lg:h-[64vh] grid place-items-center">
                        <div className="absolute inset-0 grid place-items-center">
                            <div className="h-[70%] aspect-square rounded-full bg-white/50 blur-2xl" />
                        </div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, scale: 0.9, rotate: -4, y: 30 }}
                                animate={{ opacity: 1, scale: 1, rotate: 0, y: 0 }}
                                exit={{ opacity: 0, scale: 0.92, rotate: 4, y: -30 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                className="relative grid place-items-center"
                            >
                                <img src={resolveImage(STORY[active].img)} alt={STORY[active].product} loading="lazy" decoding="async" className="max-h-[44vh] lg:max-h-[60vh] w-auto object-contain drop-shadow-[0_36px_60px_rgba(13,2,120,0.28)]" />
                                <span className="mt-4 pill">{STORY[active].product}</span>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------- page */
export default function Landing() {
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/products', { params: { featured: true } })
            .then(({ data }) => setFeatured(data.slice(0, 8)))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="overflow-x-clip">
            {/* ================= HERO ================= */}
            <section className="relative overflow-hidden bg-white">
                <div className="aurora" aria-hidden="true" />
                <div className="grain" />
                <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 lg:pt-16 pb-14 lg:pb-24 min-h-[88vh] flex items-center">
                    <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-6 items-center w-full">
                        {/* copy */}
                        <div>
                            <motion.span
                                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
                                className="pill text-[var(--xtra-blue)]"
                            >
                                <Sparkles className="h-3.5 w-3.5 text-[var(--xtra-yellow-deep)]" /> Since 1987 · India ka Xtra
                            </motion.span>

                            <h1 className="mt-5 display-hero text-[13vw] leading-[0.86] sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
                                <KineticText text="A little" delay={0.08} />
                                <KineticText text="XTRA" className="text-[var(--xtra-blue)]" delay={0.2} />
                                <br className="hidden sm:block" />
                                <KineticText text="in every wash." delay={0.34} />
                            </h1>

                            <motion.p
                                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6, ease: EASE }}
                                className="mt-6 text-base sm:text-lg text-[var(--text-muted)] max-w-xl leading-relaxed"
                            >
                                Whatever you pay, XTRA pays back more — more clean, more value, more care.
                                Premium home-care, trusted by Indian families for over three decades.
                            </motion.p>

                            <motion.div
                                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72, duration: 0.6, ease: EASE }}
                                className="mt-8 flex flex-wrap items-center gap-3"
                            >
                                <Magnetic strength={0.35}>
                                    <Link to="/shop" data-testid="hero-shop-bestsellers-button" className="btn-accent h-13 px-7 text-[15px]">
                                        Shop the range <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </Magnetic>
                                <a href="#story" className="btn-outline h-13 px-7 text-[15px]">Explore by room</a>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.6 }}
                                className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm"
                            >
                                <span className="inline-flex items-center gap-2 font-medium"><Truck className="h-4 w-4 text-[var(--xtra-blue)]" /> Free shipping over ₹499</span>
                                <span className="inline-flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4 text-[var(--xtra-blue)]" /> Kills 99.9% germs</span>
                                <span className="inline-flex items-center gap-2 font-medium"><BadgeCheck className="h-4 w-4 text-[var(--xtra-blue)]" /> Trusted since 1987</span>
                            </motion.div>
                        </div>

                        {/* montage */}
                        <Parallax offset={26}>
                            <HeroMontage />
                        </Parallax>
                    </div>
                </div>

                {/* scroll cue */}
                <motion.a
                    href="#categories" aria-label="Scroll to explore"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.6 }}
                    className="hidden lg:flex absolute left-1/2 -translate-x-1/2 bottom-6 flex-col items-center gap-1 text-[var(--text-muted)]"
                >
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">Scroll</span>
                    <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}><ArrowDown className="h-4 w-4" /></motion.span>
                </motion.a>
            </section>

            {/* ================= MARQUEE ================= */}
            <section className="bg-[var(--xtra-blue)] text-white py-5 sm:py-7 overflow-hidden pause-on-hover" aria-hidden="true">
                <div className="marquee-mask">
                    <div className="marquee-track a">
                        {[0, 1].map((k) => (
                            <span key={k} className="inline-flex items-center">
                                {MARQUEE.map((t, i) => (
                                    <span key={i} className="inline-flex items-center font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl mx-6">
                                        {t} <span className="text-[var(--xtra-yellow)] mx-6">✦</span>
                                    </span>
                                ))}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CATEGORY BENTO ================= */}
            <section id="categories" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p className="kicker text-[var(--xtra-blue)]">Shop by need</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl">Everything your home needs</h2>
                        </div>
                        <Link to="/shop" className="inline-flex items-center gap-1.5 font-semibold text-[var(--xtra-blue)] link-underline">View all products <ArrowRight className="h-4 w-4" /></Link>
                    </div>
                </Reveal>

                <div className="mt-9 grid grid-cols-2 lg:grid-cols-12 lg:auto-rows-[220px] gap-4">
                    {CATEGORIES.map((c, i) => (
                        <Reveal key={c.name} delay={i * 0.05} className={`${c.span} ${c.big ? 'col-span-2' : ''}`}>
                            <Link
                                to={c.to}
                                data-testid={`category-card-${i}`}
                                className="group relative block h-full min-h-[220px] rounded-[var(--radius-lg)] overflow-hidden border border-[var(--border)] tile-stage shadow-[var(--shadow-tight)] hover:shadow-[var(--shadow-soft)] transition-shadow duration-300"
                            >
                                <img
                                    src={resolveImage(c.image)}
                                    alt={c.name}
                                    loading="lazy"
                                    decoding="async"
                                    className={`absolute z-10 object-contain drop-shadow-[0_20px_34px_rgba(13,2,120,0.20)] transition-transform duration-500 group-hover:scale-[1.06] group-hover:-rotate-1 ${c.big ? 'right-4 bottom-2 h-[80%]' : 'right-0 bottom-1 h-[86%]'}`}
                                />
                                <div className="relative z-20 p-5 sm:p-6 h-full flex flex-col justify-end">
                                    <h3 className={`font-display font-extrabold text-[var(--xtra-blue-ink)] ${c.big ? 'text-2xl sm:text-3xl' : 'text-lg'}`}>{c.name}</h3>
                                    <p className="mt-1 max-w-[58%] text-sm text-[var(--text-muted)]">{c.desc}</p>
                                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[var(--xtra-blue)]">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ================= BESTSELLERS ================= */}
            <section className="bg-[var(--bg-soft)] section-wash">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20 lg:py-28">
                    <Reveal>
                        <div className="flex flex-wrap items-end justify-between gap-4">
                            <div>
                                <p className="kicker text-[var(--xtra-blue)]">Bestsellers</p>
                                <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl">Loved in homes across India</h2>
                            </div>
                            <Link to="/shop" className="inline-flex items-center gap-1.5 font-semibold text-[var(--xtra-blue)] link-underline">Shop all <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                    </Reveal>
                    <div className="mt-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {loading
                            ? Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-[var(--radius-lg)]" />)
                            : featured.map((p, i) => (
                                <Reveal key={p.id} delay={(i % 4) * 0.06}>
                                    <ProductCard product={p} index={i} />
                                </Reveal>
                            ))}
                    </div>
                </div>
            </section>

            {/* ================= PILLARS ================= */}
            <Pillars />

            {/* ================= COMPARISON ================= */}
            <Comparison />

            {/* ================= SAVINGS CALCULATOR ================= */}
            <SavingsCalculator />

            {/* ================= STICKY STORY ================= */}
            <div id="story"><StickyStory /></div>

            {/* ================= HERITAGE ================= */}
            <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
                <div className="relative rounded-[var(--radius-lg)] overflow-hidden royal-panel px-6 sm:px-10 lg:px-16 py-14 lg:py-20">
                    <div className="noise-overlay" />
                    <div className="relative grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
                        <div>
                            <p className="kicker text-[var(--xtra-yellow)]">Since 1987</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[0.98] text-3xl sm:text-4xl lg:text-5xl text-white">A legacy of trust,<br />built one home at a time.</h2>
                            <p className="mt-5 text-white/70 max-w-lg leading-relaxed">Three decades of honest formulas and honest pricing. From a single detergent to a full home-care range — the promise stays the same.</p>
                            <Link to="/shop" className="btn-accent mt-8 h-12 px-7 inline-flex">Discover the range <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4 sm:gap-6">
                            {[
                                { to: 3, suffix: ' Cr+', label: 'Happy homes' },
                                { to: 37, suffix: '+', label: 'Years of trust' },
                                { to: 250, suffix: '+', label: 'Cities served' },
                                { to: 99, suffix: '.9%', label: 'Germs killed' },
                            ].map((s) => (
                                <div key={s.label} className="rounded-2xl bg-white/10 border border-white/15 p-5 sm:p-6 text-white">
                                    <div className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tabular-nums">
                                        <CountUp to={s.to} suffix={s.suffix} />
                                    </div>
                                    <p className="mt-1.5 text-sm text-white/70">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= TESTIMONIALS ================= */}
            <section id="reviews" className="py-16 sm:py-20 lg:py-24 overflow-hidden">
                <Reveal>
                    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p className="kicker text-[var(--xtra-blue)]">Reviews</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-4xl lg:text-5xl">Real homes, real sparkle</h2>
                        </div>
                        <div className="flex items-center gap-3 rounded-full bg-[var(--bg-soft)] px-5 py-3">
                            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-[var(--xtra-yellow)] text-[var(--xtra-yellow)]" />)}</div>
                            <span className="font-bold tabular-nums">4.9</span>
                            <span className="text-sm text-[var(--text-muted)]">from 12,000+ reviews</span>
                        </div>
                    </div>
                </Reveal>

                <div className="mt-10 marquee-mask pause-on-hover">
                    <div className="marquee-track a">
                        {[0, 1].map((k) => (
                            <span key={k} className="inline-flex gap-5 pr-5">
                                {TESTIMONIALS.map((t, i) => (
                                    <span key={i} className="inline-flex w-[340px] whitespace-normal align-top flex-col rounded-[var(--radius-lg)] bg-white border border-[var(--border)] p-6 shadow-[var(--shadow-tight)]">
                                        <Quote className="h-6 w-6 text-[var(--xtra-yellow-deep)]" />
                                        <span className="mt-3 flex gap-0.5">{Array.from({ length: 5 }).map((_, m) => <Star key={m} className="h-3.5 w-3.5 fill-[var(--xtra-yellow)] text-[var(--xtra-yellow)]" />)}</span>
                                        <span className="mt-3 text-[15px] leading-relaxed text-black">{t.text}</span>
                                        <span className="mt-4 pt-4 border-t border-[var(--border)] block"><span className="font-semibold text-sm block">{t.name}</span><span className="text-xs text-[var(--text-muted)]">{t.city}</span></span>
                                    </span>
                                ))}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CTA BAND ================= */}
            <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-16 lg:pb-24">
                <Reveal>
                    <div className="relative rounded-[var(--radius-lg)] overflow-hidden bg-[var(--xtra-yellow)] px-6 sm:px-12 py-14 lg:py-20 text-center">
                        <div className="noise-overlay" />
                        <div className="relative">
                            <p className="kicker text-[var(--xtra-blue-ink)]">Daam wahi. Dum xtra.</p>
                            <h2 className="mt-3 font-display font-extrabold tracking-tight text-black text-4xl sm:text-6xl lg:text-7xl">India Ka Xtra.</h2>
                            <p className="mt-4 text-black/70 max-w-xl mx-auto text-lg">Give your home the extra it deserves. Try the full XTRA range today.</p>
                            <Magnetic strength={0.3}>
                                <Link to="/shop" className="btn-primary mt-8 h-13 px-8 text-[15px] inline-flex">Start shopping <ArrowRight className="h-4 w-4" /></Link>
                            </Magnetic>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
